import { useState, useEffect, useRef } from 'react'
import { supabase } from './supabase'
import BundleDetail from './BundleDetail'
import NewBundle from './NewBundle'
import Cart from './Cart'
import Account from './Account'
import Plan from './Plan'
import QuickShop from './QuickShop'
import Explore from './Explore'
import Assistant from './Assistant'

const POPULAR_BUNDLES = [
  { name: 'Taco Tuesday', emoji: '🌮', category: 'dinner', items: 8, price: 18, bg: '#FFF8E7' },
  { name: 'Spaghetti night', emoji: '🍝', category: 'dinner', items: 7, price: 14, bg: '#FFF5EC' },
  { name: 'Burger night', emoji: '🍔', category: 'dinner', items: 6, price: 20, bg: '#FFF0F0' },
  { name: 'Chicken stir fry', emoji: '🥘', category: 'dinner', items: 9, price: 16, bg: '#F0FFF4' },
  { name: 'Sheet pan fajitas', emoji: '🫑', category: 'dinner', items: 8, price: 17, bg: '#F5FFF0' },
  { name: 'Homemade pizza night', emoji: '🍕', category: 'dinner', items: 10, price: 15, bg: '#FFF5EC' },
  { name: 'Chicken & rice', emoji: '🍗', category: 'dinner', items: 6, price: 12, bg: '#FFF8E7' },
  { name: 'Turkey & avocado wrap', emoji: '🌯', category: 'lunch', items: 5, price: 10, bg: '#F5FFF0' },
  { name: 'BLT sandwiches', emoji: '🥪', category: 'lunch', items: 5, price: 9, bg: '#FFF5EC' },
  { name: 'Quesadillas', emoji: '🫓', category: 'lunch', items: 6, price: 10, bg: '#FFF8E7' },
  { name: 'Chicken caesar salad', emoji: '🥗', category: 'lunch', items: 7, price: 13, bg: '#F0F8FF' },
  { name: 'Eggs & toast', emoji: '🍳', category: 'breakfast', items: 4, price: 6, bg: '#FFF8E7' },
  { name: 'Pancake Sunday', emoji: '🥞', category: 'breakfast', items: 6, price: 8, bg: '#FFF5EC' },
  { name: 'Overnight oats', emoji: '🥣', category: 'breakfast', items: 5, price: 7, bg: '#F0FFF4' },
  { name: 'Smoothie station', emoji: '🥤', category: 'breakfast', items: 7, price: 12, bg: '#F0F8FF' },
  { name: 'Weekly snacks', emoji: '🍿', category: 'misc', items: 6, price: 14, bg: '#F5F0FF' },
  { name: 'Cleaning supplies', emoji: '🧹', category: 'misc', items: 8, price: 24, bg: '#F0FFF4' },
]

const DEFAULT_TABS = ['dinner', 'lunch', 'breakfast', 'misc']

function stripBrand(name, brand) {
  if (!brand || !name) return name || ''
  const escaped = brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return name.replace(new RegExp('^' + escaped + '\\s*', 'i'), '').trim() || name
}

export default function Home({ user, onSignOut }) {
  const [activeTab, setActiveTab] = useState('dinner')
  const [myBundles, setMyBundles] = useState([])
  const [folders, setFolders] = useState([])
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const [selectedBundle, setSelectedBundle] = useState(null)
  const [showNewBundle, setShowNewBundle] = useState(false)
  const [showCart, setShowCart] = useState(false)
  const [customTabs, setCustomTabs] = useState([])
  const [householdId, setHouseholdId] = useState(null)
  const [addingTab, setAddingTab] = useState(false)
  const [newTabName, setNewTabName] = useState('')
  const [showAccount, setShowAccount] = useState(false)
  const [showPlan, setShowPlan] = useState(false)
  const [showQuickShop, setShowQuickShop] = useState(false)
  const [showExplore, setShowExplore] = useState(false)
  const [showAssistant, setShowAssistant] = useState(false)
  const newTabInputRef = useRef(null)
  const [editingOrder, setEditingOrder] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [addingToCart, setAddingToCart] = useState(null)
  const searchInputRef = useRef(null)

  // Folder state
  const [folderModal, setFolderModal] = useState(null) // null | { mode: 'create' } | { mode: 'edit', folder }
  const [folderName, setFolderName] = useState('')
  const [folderSelectedIds, setFolderSelectedIds] = useState(new Set())
  const [bundleFolderPicker, setBundleFolderPicker] = useState(null) // bundle id
  const folderNameInputRef = useRef(null)

  useEffect(() => {
    fetchProfile()
    fetchMyBundles()
    fetchHousehold()
  }, [])

  useEffect(() => {
    if (addingTab && newTabInputRef.current) newTabInputRef.current.focus()
  }, [addingTab])

  useEffect(() => {
    if (searchOpen && searchInputRef.current) searchInputRef.current.focus()
  }, [searchOpen])

  useEffect(() => {
    if (folderModal && folderNameInputRef.current) {
      setTimeout(() => folderNameInputRef.current?.focus(), 50)
    }
  }, [folderModal])

  async function fetchProfile() {
    const { data } = await supabase.from('profiles').select('*').eq('id', user.id).single()
    setProfile(data)
  }

  async function fetchMyBundles() {
    const { data } = await supabase
      .from('bundles').select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true })
    setMyBundles(data || [])
    setLoading(false)
  }

  async function fetchHousehold() {
    const { data: membership } = await supabase
      .from('household_members').select('household_id').eq('user_id', user.id).single()
    if (!membership) return
    setHouseholdId(membership.household_id)
    const { data: household } = await supabase
      .from('households').select('custom_categories').eq('id', membership.household_id).single()
    if (household?.custom_categories) setCustomTabs(household.custom_categories)
    fetchFolders(membership.household_id)
  }

  async function fetchFolders(hid) {
    const { data } = await supabase
      .from('bundle_folders').select('*')
      .eq('household_id', hid)
      .order('created_at', { ascending: true })
    setFolders(data || [])
  }

  async function saveBundle(bundle) {
    const { data: membership } = await supabase
      .from('household_members').select('household_id').eq('user_id', user.id).single()
    if (!membership) return null
    const { data: maxData } = await supabase
      .from('bundles').select('sort_order')
      .eq('household_id', membership.household_id).eq('category', bundle.category)
      .order('sort_order', { ascending: false }).limit(1)
    const nextSortOrder = (maxData?.[0]?.sort_order ?? -1) + 1
    const { data: newBundle } = await supabase.from('bundles').insert({
      name: bundle.name, emoji: bundle.emoji, category: bundle.category,
      bg: bundle.bg, items: bundle.items, price: bundle.price,
      household_id: membership.household_id, created_by: user.id,
      is_popular: true, sort_order: nextSortOrder,
    }).select().single()
    if (newBundle) {
      setMyBundles(prev => [...prev, newBundle])
      return newBundle
    }
    return null
  }

  async function moveBundle(bundle, direction) {
    const tabBundles = myBundles.filter(b => b.category === bundle.category && !b.folder_id)
    const idx = tabBundles.findIndex(b => b.id === bundle.id)
    const newIdx = idx + direction
    if (newIdx < 0 || newIdx >= tabBundles.length) return
    const reordered = [...tabBundles]
    const [moved] = reordered.splice(idx, 1)
    reordered.splice(newIdx, 0, moved)
    const otherBundles = myBundles.filter(b => !(b.category === bundle.category && !b.folder_id))
    setMyBundles([...reordered.map((b, i) => ({ ...b, sort_order: i })), ...otherBundles])
    await Promise.all(reordered.map((b, i) =>
      supabase.from('bundles').update({ sort_order: i }).eq('id', b.id)
    ))
  }

  async function handleAddTab() {
    const trimmed = newTabName.trim().toLowerCase()
    if (!trimmed) { setAddingTab(false); return }
    const allExisting = [...DEFAULT_TABS, ...customTabs]
    if (allExisting.includes(trimmed)) {
      setActiveTab(trimmed); setAddingTab(false); setNewTabName(''); return
    }
    const updated = [...customTabs, trimmed]
    setCustomTabs(updated)
    setActiveTab(trimmed); setAddingTab(false); setNewTabName('')
    if (householdId) {
      await supabase.from('households').update({ custom_categories: updated }).eq('id', householdId)
    }
  }

  function handleTabKeyDown(e) {
    if (e.key === 'Enter') handleAddTab()
    if (e.key === 'Escape') { setAddingTab(false); setNewTabName('') }
  }

  async function addBundleToCart(bundle) {
    if (!bundle.id) return
    await supabase.from('bundles').update({ in_cart: true }).eq('id', bundle.id)
    await supabase.from('bundle_items').update({ in_cart: true, checked: false }).eq('bundle_id', bundle.id)
    setMyBundles(prev => prev.map(b => b.id === bundle.id ? { ...b, in_cart: true } : b))
  }

  async function handleSearch(q) {
    setSearchQuery(q)
    if (q.length < 2) { setSearchResults([]); return }
    setSearchLoading(true)
    try {
      const res = await fetch('/api/kroger?query=' + encodeURIComponent(q))
      const data = await res.json()
      setSearchResults((data.data || []).slice(0, 5).map(p => ({
        type: 'product',
        name: stripBrand(p.description, p.brand),
        brand: p.brand || '',
        size: p.items?.[0]?.size || '',
        price: p.items?.[0]?.price?.regular || 0,
        kroger_sku: p.productId,
      })))
    } catch { setSearchResults([]) }
    setSearchLoading(false)
  }

  const bundleSearchResults = searchQuery.length > 1
    ? myBundles.filter(b => b.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : []

  async function addProductToCart(product) {
    setAddingToCart(product.name)
    const { data: membership } = await supabase
      .from('household_members').select('household_id').eq('user_id', user.id).single()
    if (!membership) { setAddingToCart(null); return }
    let quickBundle = myBundles.find(b => b.name === 'Quick adds')
    if (!quickBundle) {
      const { data: nb } = await supabase.from('bundles').insert({
        name: 'Quick adds', emoji: '🛒', category: 'misc', bg: '#F5F5F5',
        items: 0, price: 0, household_id: membership.household_id,
        created_by: user.id, is_popular: false, in_cart: true,
      }).select().single()
      quickBundle = nb
      setMyBundles(prev => [nb, ...prev])
    } else {
      await supabase.from('bundles').update({ in_cart: true }).eq('id', quickBundle.id)
    }
    await supabase.from('bundle_items').insert({
      bundle_id: quickBundle.id, name: product.name, brand: product.brand,
      size: product.size, price: product.price, emoji: '🛒',
      quantity: 1, kroger_sku: product.kroger_sku || null,
    })
    setMyBundles(prev => prev.map(b =>
      b.id === quickBundle.id ? { ...b, items: (b.items || 0) + 1, in_cart: true } : b
    ))
    setAddingToCart(null)
    setSearchOpen(false)
    setSearchQuery('')
    setSearchResults([])
  }

  // --- Folder functions ---

  function openCreateFolder() {
    setFolderName('')
    setFolderSelectedIds(new Set())
    setFolderModal({ mode: 'create' })
  }

  function openEditFolder(folder) {
    const currentIds = new Set(myBundles.filter(b => b.folder_id === folder.id).map(b => b.id))
    setFolderName(folder.name)
    setFolderSelectedIds(currentIds)
    setFolderModal({ mode: 'edit', folder })
  }

  function closeFolderModal() {
    setFolderModal(null)
    setFolderName('')
    setFolderSelectedIds(new Set())
  }

  function toggleFolderBundleId(id) {
    setFolderSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function createFolder() {
    if (!folderName.trim() || !householdId) return
    const { data: newFolder } = await supabase.from('bundle_folders').insert({
      household_id: householdId,
      name: folderName.trim(),
      category: activeTab,
      collapsed: false,
    }).select().single()
    if (!newFolder) return
    setFolders(prev => [...prev, newFolder])
    const ids = [...folderSelectedIds]
    if (ids.length > 0) {
      await Promise.all(ids.map(bid =>
        supabase.from('bundles').update({ folder_id: newFolder.id }).eq('id', bid)
      ))
      setMyBundles(prev => prev.map(b =>
        folderSelectedIds.has(b.id) ? { ...b, folder_id: newFolder.id } : b
      ))
    }
    closeFolderModal()
  }

  async function updateFolder() {
    const folder = folderModal.folder
    if (!folderName.trim()) return
    await supabase.from('bundle_folders').update({ name: folderName.trim() }).eq('id', folder.id)
    setFolders(prev => prev.map(f => f.id === folder.id ? { ...f, name: folderName.trim() } : f))
    const currentIds = myBundles.filter(b => b.folder_id === folder.id).map(b => b.id)
    const toRemove = currentIds.filter(id => !folderSelectedIds.has(id))
    const toAdd = [...folderSelectedIds].filter(id => !currentIds.includes(id))
    await Promise.all([
      ...toRemove.map(id => supabase.from('bundles').update({ folder_id: null }).eq('id', id)),
      ...toAdd.map(id => supabase.from('bundles').update({ folder_id: folder.id }).eq('id', id)),
    ])
    setMyBundles(prev => prev.map(b => {
      if (toRemove.includes(b.id)) return { ...b, folder_id: null }
      if (toAdd.includes(b.id)) return { ...b, folder_id: folder.id }
      return b
    }))
    closeFolderModal()
  }

  async function deleteFolder(folder) {
    await supabase.from('bundle_folders').delete().eq('id', folder.id)
    setFolders(prev => prev.filter(f => f.id !== folder.id))
    setMyBundles(prev => prev.map(b => b.folder_id === folder.id ? { ...b, folder_id: null } : b))
    closeFolderModal()
  }

  async function toggleFolderCollapse(folder) {
    const newVal = !folder.collapsed
    setFolders(prev => prev.map(f => f.id === folder.id ? { ...f, collapsed: newVal } : f))
    await supabase.from('bundle_folders').update({ collapsed: newVal }).eq('id', folder.id)
  }

  async function moveBundleToFolder(bundleId, folderId) {
    await supabase.from('bundles').update({ folder_id: folderId }).eq('id', bundleId)
    setMyBundles(prev => prev.map(b => b.id === bundleId ? { ...b, folder_id: folderId } : b))
    setBundleFolderPicker(null)
  }

  // --- Derived ---
  const cartCount = myBundles.filter(b => b.in_cart).reduce((sum, b) => sum + (b.items || 0), 0)
  const allTabs = [...DEFAULT_TABS, ...customTabs]
  const isCustomTab = !DEFAULT_TABS.includes(activeTab)
  const popularInTab = POPULAR_BUNDLES.filter(b => b.category === activeTab)
  const myBundlesInTab = myBundles.filter(b => b.category === activeTab)
  const foldersInTab = folders.filter(f => f.category === activeTab)
  const ungroupedInTab = myBundlesInTab.filter(b => !b.folder_id)

  if (selectedBundle) {
    return (
      <BundleDetail
        bundle={selectedBundle}
        customTabs={customTabs}
        onBack={() => setSelectedBundle(null)}
        onDelete={(bundle) => { setMyBundles(prev => prev.filter(b => b.id !== bundle.id)); setSelectedBundle(null) }}
        onCategoryChange={(id, newCategory) => {
          setMyBundles(prev => prev.map(b => b.id === id ? { ...b, category: newCategory } : b))
        }}
        onBundleUpdate={(id, changes) => {
          setMyBundles(prev => prev.map(b => b.id === id ? { ...b, ...changes } : b))
        }}
      />
    )
  }

  if (showNewBundle) {
    return (
      <NewBundle
        user={user} customTabs={customTabs}
        onBack={() => setShowNewBundle(false)}
        defaultCategory={isCustomTab ? activeTab : undefined}
        onSaved={(bundle) => { setMyBundles(prev => [...prev, bundle]); setShowNewBundle(false) }}
      />
    )
  }

  if (showCart) return <Cart user={user} onBack={() => { setShowCart(false); fetchMyBundles() }} />
  if (showQuickShop) return <QuickShop user={user} onBack={() => { setShowQuickShop(false); fetchMyBundles() }} onGoToCart={() => { setShowQuickShop(false); setShowCart(true) }} />
  if (showPlan) return <Plan user={user} onBack={() => setShowPlan(false)} onGoToCart={() => { setShowPlan(false); setShowCart(true) }} />
  if (showExplore) return (
    <Explore user={user} customTabs={customTabs} onBack={() => setShowExplore(false)}
      onSaved={(bundle) => { setMyBundles(prev => [...prev, bundle]); setShowExplore(false) }}
    />
  )
  if (showAccount) return <Account user={user} profile={profile} onBack={() => setShowAccount(false)} onSignOut={onSignOut} />
  if (showAssistant) return <Assistant user={user} customTabs={customTabs} onBack={() => { setShowAssistant(false); fetchMyBundles() }} />

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <div style={styles.brand}>bun<span style={styles.brandGreen}>dl</span>e</div>
        <button onClick={() => setShowAccount(true)} style={styles.signOutBtn}>
          {profile?.name || 'Account'} ↓
        </button>
      </div>

      {searchOpen ? (
        <div style={styles.searchContainer}>
          <div style={styles.searchBarActive}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              ref={searchInputRef}
              style={styles.searchInput}
              placeholder="Search bundles & items…"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
            />
            {searchLoading && <span style={{ color: '#aaa', fontSize: '12px' }}>…</span>}
            <button style={styles.searchCancelBtn} onClick={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]) }}>Cancel</button>
          </div>
          {(bundleSearchResults.length > 0 || searchResults.length > 0) && (
            <div style={styles.searchDropdown}>
              {bundleSearchResults.length > 0 && (
                <>
                  <div style={styles.searchSectionLabel}>Your bundles</div>
                  {bundleSearchResults.map(bundle => (
                    <div key={bundle.id} style={styles.searchResultRow} onClick={() => { setSelectedBundle(bundle); setSearchOpen(false); setSearchQuery(''); setSearchResults([]) }}>
                      <span style={styles.searchResultEmoji}>{bundle.emoji}</span>
                      <div style={styles.searchResultInfo}>
                        <div style={styles.searchResultName}>{bundle.name}</div>
                        <div style={styles.searchResultMeta}>{bundle.items} items · ~${bundle.price}</div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              {searchResults.length > 0 && (
                <>
                  {bundleSearchResults.length > 0 && <div style={styles.searchDivider} />}
                  <div style={styles.searchSectionLabel}>Store results</div>
                  {searchResults.map((item, i) => (
                    <div key={i} style={styles.searchResultRow} onClick={() => addProductToCart(item)}>
                      <span style={styles.searchResultEmoji}>🛒</span>
                      <div style={styles.searchResultInfo}>
                        <div style={styles.searchResultName}>{item.name}</div>
                        <div style={styles.searchResultMeta}>{item.brand} · {item.size}</div>
                      </div>
                      <div style={styles.searchResultRight}>
                        {addingToCart === item.name
                          ? <span style={{ fontSize: '12px', color: '#aaa' }}>…</span>
                          : <span style={styles.searchResultPrice}>${item.price?.toFixed(2)}</span>}
                        <span style={styles.addToCartLabel}>+ Cart</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <div style={styles.searchBar} onClick={() => setSearchOpen(true)}>
          <span style={styles.searchIcon}>🔍</span>
          <span style={styles.searchText}>Search bundles & items…</span>
        </div>
      )}

      <div style={styles.actionRow}>
        <button style={styles.btnQuick} onClick={() => setShowQuickShop(true)}>+ Add single item</button>
        <button style={styles.btnSec} onClick={() => setShowNewBundle(true)}>+ New bundle</button>
        <button style={styles.btnSec} onClick={() => setShowExplore(true)}>Explore bundles</button>
      </div>

      <div style={styles.tabRow}>
        {allTabs.map(tab => (
          <button
            key={tab}
            style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        {addingTab ? (
          <div style={styles.tabInputWrap}>
            <input
              ref={newTabInputRef}
              style={styles.tabInput}
              placeholder="Name…"
              value={newTabName}
              onChange={e => setNewTabName(e.target.value)}
              onKeyDown={handleTabKeyDown}
              onBlur={handleAddTab}
              maxLength={24}
            />
          </div>
        ) : (
          <button style={styles.addTabBtn} onClick={() => setAddingTab(true)} title="Add category">+</button>
        )}
      </div>

      <div style={styles.content}>
        <div style={styles.sectionHead}>
          <span style={styles.sectionTitle}>Your bundles</span>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button style={styles.editOrderBtn} onClick={openCreateFolder}>+ Folder</button>
            {ungroupedInTab.length > 1 && !editingOrder && (
              <button style={styles.editOrderBtn} onClick={() => setEditingOrder(true)}>Edit order</button>
            )}
            {editingOrder && (
              <button style={styles.editOrderBtn} onClick={() => setEditingOrder(false)}>Done</button>
            )}
          </div>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading…</div>
        ) : myBundlesInTab.length === 0 && foldersInTab.length === 0 ? (
          <div style={styles.emptyYours}>
            <div style={styles.emptyIcon}>{isCustomTab ? '✨' : '🗂️'}</div>
            <div style={styles.emptyTitle}>
              {isCustomTab ? 'Your ' + activeTab + ' bundles' : 'Your bundles live here'}
            </div>
            <div style={styles.emptySub}>
              {isCustomTab
                ? "Build a bundle and save it here — it'll show up every week."
                : "Save a popular bundle or build your own — they'll be waiting every week."}
            </div>
            <div style={styles.emptyBtns}>
              {!isCustomTab && <button style={styles.emptyBtnPrimary}>Browse popular</button>}
              <button style={styles.emptyBtnGhost} onClick={() => setShowNewBundle(true)}>Build my own</button>
            </div>
          </div>
        ) : editingOrder ? (
          ungroupedInTab.map((bundle, idx) => (
            <div key={bundle.id} style={styles.reorderRow}>
              <span style={styles.reorderEmoji}>{bundle.emoji}</span>
              <span style={styles.reorderName}>{bundle.name}</span>
              <div style={styles.reorderBtns}>
                <button style={styles.arrowBtn} onClick={() => moveBundle(bundle, -1)} disabled={idx === 0}>↑</button>
                <button style={styles.arrowBtn} onClick={() => moveBundle(bundle, 1)} disabled={idx === ungroupedInTab.length - 1}>↓</button>
              </div>
            </div>
          ))
        ) : (
          <>
            {foldersInTab.map(folder => (
              <FolderSection
                key={folder.id}
                folder={folder}
                bundles={myBundlesInTab.filter(b => b.folder_id === folder.id)}
                onToggleCollapse={() => toggleFolderCollapse(folder)}
                onEdit={() => openEditFolder(folder)}
                onSelect={setSelectedBundle}
                onAddToCart={addBundleToCart}
                onSave={saveBundle}
                foldersInTab={foldersInTab}
                onOpenFolderPicker={(bundleId) => setBundleFolderPicker(bundleId)}
              />
            ))}

            {ungroupedInTab.map(bundle => (
              <BundleCard
                key={bundle.id}
                bundle={bundle}
                saved
                onSave={saveBundle}
                onSelect={setSelectedBundle}
                onAddToCart={addBundleToCart}
                foldersInTab={foldersInTab}
                onOpenFolderPicker={() => setBundleFolderPicker(bundle.id)}
              />
            ))}
          </>
        )}

        {!isCustomTab && (
          <>
            <div style={styles.divider} />
            <div style={styles.sectionHead}>
              <span style={styles.sectionTitle}>Popular</span>
            </div>
            {popularInTab
              .filter(bundle => !myBundles.some(b => b.name === bundle.name))
              .map(bundle => (
                <BundleCard key={bundle.name} bundle={bundle} onSave={saveBundle} onSelect={setSelectedBundle} />
              ))}
          </>
        )}
      </div>

      <div style={styles.bottomNav}>
        <NavItem icon="🗂️" label="Bundles" active />
        <NavItem icon="📅" label="Plan" onClick={() => setShowPlan(true)} />
        <NavItem icon="🛒" label="Cart" onClick={() => setShowCart(true)} badge={cartCount} />
        <NavItem icon="🤵" label="Assistant" onClick={() => setShowAssistant(true)} />
      </div>

      {/* Folder create/edit modal */}
      {folderModal && (
        <div style={styles.modalOverlay} onClick={closeFolderModal}>
          <div style={styles.modalSheet} onClick={e => e.stopPropagation()}>
            <div style={styles.modalTitle}>
              {folderModal.mode === 'create' ? 'New folder' : 'Edit folder'}
            </div>
            <input
              ref={folderNameInputRef}
              style={styles.folderNameInput}
              placeholder="Folder name…"
              value={folderName}
              onChange={e => setFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') folderModal.mode === 'create' ? createFolder() : updateFolder() }}
            />
            {myBundlesInTab.length > 0 && (
              <>
                <div style={styles.modalSectionLabel}>
                  {folderModal.mode === 'create' ? `Bundles to add:` : 'Bundles in this folder:'}
                </div>
                <div style={styles.folderBundleList}>
                  {myBundlesInTab.map(bundle => (
                    <div
                      key={bundle.id}
                      style={styles.folderBundleRow}
                      onClick={() => toggleFolderBundleId(bundle.id)}
                    >
                      <div style={{ ...styles.folderCheckbox, ...(folderSelectedIds.has(bundle.id) ? styles.folderCheckboxOn : {}) }}>
                        {folderSelectedIds.has(bundle.id) && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <span style={{ fontSize: '16px', flexShrink: 0 }}>{bundle.emoji}</span>
                      <span style={styles.folderBundleRowName}>{bundle.name}</span>
                      {bundle.folder_id && bundle.folder_id !== folderModal.folder?.id && (
                        <span style={styles.folderBundleInOther}>
                          {folders.find(f => f.id === bundle.folder_id)?.name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            <div style={styles.modalActions}>
              {folderModal.mode === 'edit' && (
                <button style={styles.modalDeleteBtn} onClick={() => deleteFolder(folderModal.folder)}>Delete</button>
              )}
              <div style={{ flex: 1 }} />
              <button style={styles.modalCancelBtn} onClick={closeFolderModal}>Cancel</button>
              <button
                style={{ ...styles.modalSaveBtn, ...(!folderName.trim() ? { opacity: 0.4 } : {}) }}
                onClick={folderModal.mode === 'create' ? createFolder : updateFolder}
                disabled={!folderName.trim()}
              >
                {folderModal.mode === 'create' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move to folder picker */}
      {bundleFolderPicker && (
        <div style={styles.modalOverlay} onClick={() => setBundleFolderPicker(null)}>
          <div style={styles.modalSheet} onClick={e => e.stopPropagation()}>
            <div style={styles.modalTitle}>Move to folder</div>
            {foldersInTab.length === 0 && (
              <div style={styles.folderPickerEmpty}>No folders in this tab yet. Create one with "+ Folder".</div>
            )}
            {foldersInTab.map(folder => {
              const isCurrentFolder = myBundles.find(b => b.id === bundleFolderPicker)?.folder_id === folder.id
              return (
                <div
                  key={folder.id}
                  style={{ ...styles.folderPickerRow, ...(isCurrentFolder ? styles.folderPickerRowActive : {}) }}
                  onClick={() => moveBundleToFolder(bundleFolderPicker, folder.id)}
                >
                  <span style={{ fontSize: '16px' }}>📁</span>
                  <span style={styles.folderPickerName}>{folder.name}</span>
                  {isCurrentFolder && <span style={styles.folderPickerCheck}>✓</span>}
                </div>
              )
            })}
            {myBundles.find(b => b.id === bundleFolderPicker)?.folder_id && (
              <div style={{ ...styles.folderPickerRow, borderTop: '0.5px solid #eee', marginTop: '4px' }}
                onClick={() => moveBundleToFolder(bundleFolderPicker, null)}>
                <span style={{ fontSize: '16px' }}>✕</span>
                <span style={{ ...styles.folderPickerName, color: '#888' }}>Remove from folder</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function FolderSection({ folder, bundles, onToggleCollapse, onEdit, onSelect, onAddToCart, onSave, foldersInTab, onOpenFolderPicker }) {
  return (
    <div style={styles.folderSection}>
      <div style={styles.folderHeader}>
        <div style={styles.folderHeaderLeft} onClick={onToggleCollapse}>
          <span style={styles.folderChevron}>{folder.collapsed ? '▶' : '▼'}</span>
          <span style={{ fontSize: '15px' }}>📁</span>
          <span style={styles.folderName}>{folder.name}</span>
          <span style={styles.folderCount}>{bundles.length}</span>
        </div>
        <button style={styles.folderEditBtn} onClick={onEdit}>✏️</button>
      </div>
      {!folder.collapsed && (
        <div style={styles.folderContent}>
          {bundles.length === 0 ? (
            <div style={styles.folderEmpty}>No bundles yet — edit this folder to add some.</div>
          ) : (
            bundles.map(bundle => (
              <BundleCard
                key={bundle.id}
                bundle={bundle}
                saved
                onSave={onSave}
                onSelect={onSelect}
                onAddToCart={onAddToCart}
                foldersInTab={foldersInTab}
                onOpenFolderPicker={() => onOpenFolderPicker(bundle.id)}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

function BundleCard({ bundle, saved, onSave, onSelect, onAddToCart, foldersInTab, onOpenFolderPicker }) {
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const [addedToCart, setAddedToCart] = useState(false)

  async function handleSave(e) {
    e.stopPropagation()
    if (done) return
    setSaving(true)
    await onSave(bundle)
    setDone(true)
    setSaving(false)
  }

  async function handleAddToCart(e) {
    e.stopPropagation()
    if (addedToCart) return
    await onAddToCart(bundle)
    setAddedToCart(true)
    setTimeout(() => setAddedToCart(false), 2000)
  }

  async function handleClick() {
    if (bundle.id) {
      onSelect && onSelect(bundle)
    } else {
      setSaving(true)
      const savedBundle = await onSave(bundle)
      setSaving(false)
      if (savedBundle) onSelect && onSelect(savedBundle)
    }
  }

  if (saved) {
    return (
      <div style={styles.bundleCard} onClick={handleClick}>
        <div style={{ ...styles.bundleEmoji, background: bundle.bg || '#F5F5F5' }}>{bundle.emoji}</div>
        <div style={styles.bundleInfo}>
          <div style={styles.bundleName}>{bundle.name}</div>
          <div style={styles.bundleMeta}>{bundle.items} items · ~${bundle.price} per meal</div>
        </div>
        <div style={styles.bundleRight}>
          <button
            style={{ ...styles.addBtn, ...(addedToCart ? { background: '#1D9E75', borderColor: '#1D9E75', color: 'white', fontSize: '14px' } : {}) }}
            onClick={handleAddToCart}
          >
            {addedToCart ? '✓' : '+'}
          </button>
          {foldersInTab && foldersInTab.length > 0 && onOpenFolderPicker && (
            <button
              style={styles.folderMoveBtn}
              onClick={e => { e.stopPropagation(); onOpenFolderPicker() }}
              title="Move to folder"
            >
              📁
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={styles.bundleCard} onClick={handleClick}>
      <div style={{ ...styles.bundleEmoji, background: bundle.bg || '#F5F5F5' }}>{bundle.emoji}</div>
      <div style={styles.bundleInfo}>
        <div style={styles.bundleName}>{bundle.name}</div>
        <div style={styles.bundleMeta}>{bundle.items} items · ~${bundle.price} per meal</div>
      </div>
      <div style={styles.bundleRight}>
        <button
          style={{ ...styles.addBtn, ...(done ? { background: '#1D9E75', borderColor: '#1D9E75', color: 'white', fontSize: '14px' } : {}) }}
          onClick={handleSave}
        >
          {saving ? '…' : done ? '✓' : '+'}
        </button>
        <span style={done ? styles.badgeSaved : styles.badgePop}>{done ? 'Saved' : 'Popular'}</span>
      </div>
    </div>
  )
}

function NavItem({ icon, label, active, onClick, badge }) {
  return (
    <div style={{ ...styles.navItem, ...(active ? styles.navItemActive : {}) }} onClick={onClick}>
      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <span style={{ fontSize: '18px' }}>{icon}</span>
        {badge > 0 && <span style={styles.navBadge}>{badge > 99 ? '99+' : badge}</span>}
      </div>
      <span>{label}</span>
    </div>
  )
}

const styles = {
  container: { maxWidth: '430px', margin: '0 auto', minHeight: '100vh', background: 'white', display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif" },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px 8px', flexShrink: 0 },
  brand: { fontFamily: "'DM Serif Display', serif", fontSize: '22px', color: '#1a1a18' },
  brandGreen: { color: '#1D9E75' },
  signOutBtn: { fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#888', background: 'none', border: '0.5px solid #ddd', borderRadius: '20px', padding: '5px 12px', cursor: 'pointer' },
  searchBar: { margin: '0 20px 10px', display: 'flex', alignItems: 'center', gap: '8px', background: '#f9f9f7', border: '0.5px solid #eee', borderRadius: '12px', padding: '10px 14px', cursor: 'pointer' },
  searchIcon: { fontSize: '14px' },
  searchText: { fontSize: '13px', color: '#aaa' },
  searchContainer: { margin: '0 20px 10px', position: 'relative', zIndex: 50 },
  searchBarActive: { display: 'flex', alignItems: 'center', gap: '8px', background: '#f9f9f7', border: '0.5px solid #1D9E75', borderRadius: '12px', padding: '10px 14px' },
  searchInput: { flex: 1, background: 'none', border: 'none', outline: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: '#1a1a18' },
  searchCancelBtn: { background: 'none', border: 'none', fontSize: '12px', color: '#888', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", flexShrink: 0 },
  searchDropdown: { marginTop: '4px', background: 'white', border: '0.5px solid #eee', borderRadius: '14px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' },
  searchSectionLabel: { fontSize: '10px', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 14px 4px' },
  searchDivider: { height: '0.5px', background: '#eee', margin: '6px 0' },
  searchResultRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', borderBottom: '0.5px solid #f5f5f5' },
  searchResultEmoji: { fontSize: '18px', flexShrink: 0 },
  searchResultInfo: { flex: 1, minWidth: 0 },
  searchResultName: { fontSize: '13px', fontWeight: '500', color: '#1a1a18', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  searchResultMeta: { fontSize: '11px', color: '#888' },
  searchResultRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', flexShrink: 0 },
  searchResultPrice: { fontSize: '12px', color: '#888' },
  addToCartLabel: { fontSize: '10px', fontWeight: '500', color: '#1D9E75' },
  actionRow: { display: 'flex', gap: '8px', padding: '0 20px 12px' },
  btnQuick: { flex: 1, padding: '10px 8px', borderRadius: '12px', border: 'none', background: '#E1F5EE', color: '#0F6E56', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  btnSec: { flex: 1, padding: '10px 8px', borderRadius: '12px', border: '0.5px solid #ddd', background: '#f9f9f7', color: '#888', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  tabRow: { display: 'flex', alignItems: 'center', padding: '0 20px', borderBottom: '0.5px solid #eee', flexShrink: 0, overflowX: 'auto' },
  tab: { fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: '#aaa', padding: '8px 14px 8px 0', border: 'none', background: 'none', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: '-0.5px', whiteSpace: 'nowrap', flexShrink: 0 },
  tabActive: { color: '#1D9E75', fontWeight: '500', borderBottom: '2px solid #1D9E75' },
  addTabBtn: { marginLeft: '4px', width: '24px', height: '24px', borderRadius: '50%', border: '0.5px solid #ddd', background: '#f9f9f7', color: '#aaa', fontSize: '16px', fontWeight: '300', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, lineHeight: 1, fontFamily: "'DM Sans', sans-serif" },
  tabInputWrap: { display: 'flex', alignItems: 'center', marginLeft: '6px', flexShrink: 0 },
  tabInput: { fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: '#1a1a18', border: 'none', borderBottom: '2px solid #1D9E75', outline: 'none', background: 'transparent', width: '80px', padding: '4px 2px' },
  content: { flex: 1, overflowY: 'auto', padding: '12px 20px 8px' },
  sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  editOrderBtn: { background: 'none', border: 'none', fontSize: '12px', color: '#1D9E75', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", padding: '0' },
  reorderRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '0.5px solid #eee' },
  reorderEmoji: { fontSize: '20px' },
  reorderName: { flex: 1, fontSize: '14px', fontWeight: '500', color: '#1a1a18' },
  reorderBtns: { display: 'flex', gap: '6px' },
  arrowBtn: { width: '30px', height: '30px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#f9f9f7', fontSize: '14px', cursor: 'pointer', color: '#1a1a18', fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sectionTitle: { fontSize: '11px', fontWeight: '500', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em' },
  emptyState: { color: '#aaa', fontSize: '13px', padding: '12px 0' },
  emptyYours: { background: '#f9f9f7', border: '1.5px dashed #ddd', borderRadius: '16px', padding: '24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '8px', marginBottom: '12px' },
  emptyIcon: { fontSize: '32px' },
  emptyTitle: { fontSize: '14px', fontWeight: '500', color: '#1a1a18' },
  emptySub: { fontSize: '12px', color: '#888', lineHeight: '1.5' },
  emptyBtns: { display: 'flex', gap: '8px', width: '100%', marginTop: '4px' },
  emptyBtnPrimary: { flex: 1, padding: '9px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  emptyBtnGhost: { flex: 1, padding: '9px', background: 'none', color: '#888', border: '0.5px solid #ddd', borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer' },
  divider: { height: '0.5px', background: '#eee', margin: '4px 0 12px' },
  bundleCard: { display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 13px', border: '0.5px solid #eee', borderRadius: '14px', marginBottom: '8px', cursor: 'pointer', background: 'white' },
  bundleEmoji: { width: '44px', height: '44px', borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 },
  bundleInfo: { flex: 1, minWidth: 0 },
  bundleName: { fontSize: '14px', fontWeight: '500', color: '#1a1a18', marginBottom: '2px' },
  bundleMeta: { fontSize: '11px', color: '#888' },
  bundleRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px', flexShrink: 0 },
  addBtn: { width: '28px', height: '28px', borderRadius: '50%', border: '0.5px solid #ddd', background: '#f9f9f7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '300', color: '#888', cursor: 'pointer' },
  folderMoveBtn: { background: 'none', border: 'none', fontSize: '13px', cursor: 'pointer', padding: '0', lineHeight: 1, opacity: 0.5 },
  badgePop: { background: '#FAEEDA', color: '#633806', fontSize: '10px', fontWeight: '500', padding: '2px 7px', borderRadius: '20px' },
  badgeSaved: { background: '#E1F5EE', color: '#0F6E56', fontSize: '10px', fontWeight: '500', padding: '2px 7px', borderRadius: '20px' },
  // Folder styles
  folderSection: { marginBottom: '8px' },
  folderHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0 6px', borderBottom: '0.5px solid #eee', marginBottom: '4px' },
  folderHeaderLeft: { display: 'flex', alignItems: 'center', gap: '7px', flex: 1, cursor: 'pointer', minWidth: 0 },
  folderChevron: { fontSize: '9px', color: '#aaa', flexShrink: 0 },
  folderName: { fontSize: '13px', fontWeight: '500', color: '#1a1a18', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  folderCount: { fontSize: '11px', color: '#aaa', flexShrink: 0 },
  folderEditBtn: { background: 'none', border: 'none', fontSize: '13px', cursor: 'pointer', padding: '2px 4px', opacity: 0.5, flexShrink: 0 },
  folderContent: { paddingLeft: '0' },
  folderEmpty: { fontSize: '12px', color: '#aaa', padding: '10px 4px', fontStyle: 'italic' },
  // Modal styles
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modalSheet: { background: 'white', borderRadius: '20px 20px 0 0', padding: '24px 20px 32px', width: '100%', maxWidth: '430px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: '14px' },
  modalTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '20px', color: '#1a1a18' },
  folderNameInput: { border: 'none', borderBottom: '1.5px solid #1D9E75', outline: 'none', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", color: '#1a1a18', padding: '6px 2px', background: 'transparent', width: '100%' },
  modalSectionLabel: { fontSize: '11px', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em' },
  folderBundleList: { overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  folderBundleRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 4px', borderBottom: '0.5px solid #f5f5f5', cursor: 'pointer' },
  folderCheckbox: { width: '20px', height: '20px', borderRadius: '6px', border: '1.5px solid #ddd', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  folderCheckboxOn: { background: '#1D9E75', border: '1.5px solid #1D9E75' },
  folderBundleRowName: { flex: 1, fontSize: '13px', fontWeight: '500', color: '#1a1a18' },
  folderBundleInOther: { fontSize: '10px', color: '#aaa', background: '#f5f5f5', padding: '2px 6px', borderRadius: '10px', flexShrink: 0 },
  modalActions: { display: 'flex', gap: '10px', alignItems: 'center', paddingTop: '4px' },
  modalCancelBtn: { padding: '10px 16px', background: 'none', border: '0.5px solid #ddd', borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: '#888', cursor: 'pointer' },
  modalSaveBtn: { padding: '10px 20px', background: '#1D9E75', border: 'none', borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: '500', color: 'white', cursor: 'pointer' },
  modalDeleteBtn: { padding: '10px 14px', background: 'none', border: '0.5px solid #ffcccc', borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: '#cc4444', cursor: 'pointer' },
  // Folder picker (move to folder)
  folderPickerRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 4px', cursor: 'pointer', borderBottom: '0.5px solid #f5f5f5' },
  folderPickerRowActive: { background: '#f4fbf8', borderRadius: '8px' },
  folderPickerName: { flex: 1, fontSize: '14px', fontWeight: '500', color: '#1a1a18' },
  folderPickerCheck: { fontSize: '14px', color: '#1D9E75' },
  folderPickerEmpty: { fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '16px 0' },
  // Nav
  bottomNav: { display: 'flex', justifyContent: 'space-around', padding: '10px 0 14px', borderTop: '0.5px solid #eee', flexShrink: 0 },
  navItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', fontSize: '10px', color: '#aaa', cursor: 'pointer' },
  navItemActive: { color: '#1D9E75' },
  navBadge: { position: 'absolute', top: '-5px', right: '-8px', background: '#1D9E75', color: 'white', fontSize: '9px', fontWeight: '600', minWidth: '15px', height: '15px', borderRadius: '8px', padding: '0 3px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 },
}
